import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import type {
  CelebrationOptions,
  CelebrationContextValue,
  ActiveCelebration,
} from '@/types/celebration'
import type { Studio } from '@/types/studio'
import { prefersReducedMotion } from '@/lib/animations'
import { getStudioById } from '@/data/studios'
import { useStudioContext } from '@/lib/studio/studio-context'
import { ConfettiCelebration } from '@/components/celebration/ConfettiCelebration'

const DEFAULT_OPTIONS: Required<CelebrationOptions> = {
  intensity: 'moderate',
  colors: ['bg-purple-400', 'bg-pink-400', 'bg-blue-400', 'bg-amber-300'],
  duration: 2000,
}

const CelebrationContext = createContext<CelebrationContextValue | undefined>(
  undefined
)

interface CelebrationProviderProps {
  children: ReactNode
}

/**
 * Provider for celebration animations
 * Wraps the app to provide celebrate() function via context
 */
export function CelebrationProvider({ children }: CelebrationProviderProps) {
  const [activeCelebration, setActiveCelebration] =
    useState<ActiveCelebration | null>(null)

  // Get current studio ID from studio context
  const { currentStudioId } = useStudioContext()

  // Fetch studio settings
  const { data: studio } = useQuery({
    queryKey: ['studio', currentStudioId],
    queryFn: async (): Promise<Studio | null> => {
      if (!currentStudioId) return null
      // Mock delay for realistic behavior
      await new Promise((resolve) => setTimeout(resolve, 100))
      return getStudioById(currentStudioId) ?? null
    },
    enabled: !!currentStudioId,
    staleTime: 5 * 60 * 1000,
  })

  const isActive = activeCelebration !== null

  const celebrate = useCallback(
    (options?: CelebrationOptions) => {
      // Guard: Feature flag disabled
      if (!studio?.settings.enableCelebrations) {
        return
      }

      // Guard: User prefers reduced motion
      if (prefersReducedMotion()) {
        return
      }

      // Guard: Already celebrating
      if (isActive) {
        return
      }

      // Merge options with defaults
      const celebration: ActiveCelebration = {
        id: `celebration-${Date.now()}`,
        intensity: options?.intensity ?? DEFAULT_OPTIONS.intensity,
        colors: options?.colors ?? DEFAULT_OPTIONS.colors,
        duration: options?.duration ?? DEFAULT_OPTIONS.duration,
      }

      setActiveCelebration(celebration)
    },
    [studio?.settings.enableCelebrations, isActive]
  )

  const handleComplete = useCallback(() => {
    setActiveCelebration(null)
  }, [])

  const value = useMemo(
    () => ({
      celebrate,
      isActive,
    }),
    [celebrate, isActive]
  )

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      {activeCelebration && (
        <ConfettiCelebration
          intensity={activeCelebration.intensity}
          colors={activeCelebration.colors}
          duration={activeCelebration.duration}
          onComplete={handleComplete}
        />
      )}
    </CelebrationContext.Provider>
  )
}

/**
 * Hook to access celebration context
 * @throws Error if used outside CelebrationProvider
 */
export function useCelebration(): CelebrationContextValue {
  const context = useContext(CelebrationContext)
  if (context === undefined) {
    throw new Error('useCelebration must be used within a CelebrationProvider')
  }
  return context
}
